import { axiosInstance } from './axios'

/**
 * Downloading a file the SERVER produced.
 *
 * Every other download in this app is a Blob the browser built from data it
 * already had — see downloadCsvFile in csvExport.ts. Flowmeter reports are
 * different: the CSV must be the exact bytes that were emailed, and the PDF is
 * rendered server-side, so both have to come down the wire.
 *
 * Two things make this more than a one-liner:
 *
 *  - The axios instance sets Content-Type: application/json by default and
 *    unwraps a {success, data} envelope. Neither applies to a binary body.
 *  - On an error response the interceptor reads error.response.data as JSON,
 *    but with responseType 'blob' that data IS a Blob — so the useful message
 *    is inside it and has to be read out, or the user gets "[object Blob]".
 */

/** Hand a Blob to the browser as a file. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  // Appended to the document because Firefox ignores a click on a detached
  // anchor; the inline copies elsewhere in this codebase that skip this step
  // work only by accident of being Chrome-tested.
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Pull the real message out of a failed blob request.
 *
 * Without this every download failure reads "Request failed with status code
 * 500", discarding the API's own explanation.
 */
async function messageFromBlobError(error: any, fallback: string): Promise<string> {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const text = await data.text()
      const parsed = JSON.parse(text)
      return parsed?.error?.message || parsed?.error || parsed?.message || fallback
    } catch {
      // Not JSON — an HTML error page, or an empty body.
      return fallback
    }
  }
  return data?.error?.message || error?.message || fallback
}

/**
 * GET a file from the API and save it.
 *
 * The filename comes from the server's Content-Disposition when it sends one,
 * so a downloaded copy matches the emailed attachment; `fallbackName` covers
 * the case where it does not.
 */
export async function downloadFromApi(
  url: string,
  fallbackName: string,
  errorMessage = 'Download failed'
): Promise<void> {
  try {
    const response = await axiosInstance.get(url, { responseType: 'blob' })
    saveBlob(response.data as Blob, filenameFrom(response.headers, fallbackName))
  } catch (error) {
    throw new Error(await messageFromBlobError(error, errorMessage))
  }
}

/** filename="..." out of a Content-Disposition header. */
export function filenameFrom(headers: any, fallback: string): string {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition']
  if (typeof disposition === 'string') {
    const quoted = disposition.match(/filename="([^"]+)"/)
    if (quoted?.[1]) return quoted[1]
    const bare = disposition.match(/filename=([^;]+)/)
    if (bare?.[1]) return bare[1].trim()
  }
  return fallback
}
